import {
  FilterAttributes,
  FilterTypes,
  SystemFilterType,
  isTagConditionalFormatting,
  isConditionalFormatting,
  isSystemFilter,
  FormattingFilter,
  FilterInterfaceFilter,
} from '@ardoq/api-types';
import { vizFilterGray } from '@ardoq/design-tokens';

export const createSystemFilter = (
  affectComponent: boolean,
  affectReference: boolean,
  name: SystemFilterType
): FilterAttributes => ({
  type: FilterTypes.SYSTEM_FILTER,
  affectComponent,
  affectReference,
  color: vizFilterGray,
  name,
  isNegative: false,
  value: '',
});

type HasEntityConditionalFormattingArgs = {
  applicableRules: FilterAttributes[];
  affectComponent: boolean;
  affectReference: boolean;
};

/**
 * Checks if there are any conditional formatting rules that apply to either components or references based on the provided criteria.
 * @param applicableRules
 * @param affectComponent
 * @param affectReference
 */
const hasEntityConditionalFormatting = ({
  applicableRules,
  affectComponent,
  affectReference,
}: HasEntityConditionalFormattingArgs): boolean =>
  applicableRules.some(
    rule =>
      isTagConditionalFormatting(rule) ||
      (isConditionalFormatting(rule) &&
        !isSystemFilter(rule) &&
        rule.affectComponent === affectComponent &&
        rule.affectReference === affectReference)
  );

type CalculateSystemFilterChangesArgs = {
  conditionalFormattingRules: FilterAttributes[];
  shouldGreyOut: boolean;
};

/**
 * Determines which system filters need to be added based on the provided conditional formatting rules and whether graying out is required.
 * It checks for both component and reference related formatting rules and adds the appropriate system filters accordingly.
 *
 * @param conditionalFormattingRules - The list of current conditional formatting rules to evaluate.
 * @param shouldGreyOut - A boolean indicating whether components/references should be grayed out based on the presence of formatting rules.
 * @returns An array of FilterAttributes representing the system filters that need to be added.
 */
export const calculateSystemFilters = ({
  conditionalFormattingRules,
  shouldGreyOut,
}: CalculateSystemFilterChangesArgs): FilterAttributes[] => {
  if (!shouldGreyOut) {
    return [];
  }

  const hasComponentsFormatting = hasEntityConditionalFormatting({
    applicableRules: conditionalFormattingRules,
    affectComponent: true,
    affectReference: false,
  });
  const hasReferencesFormatting = hasEntityConditionalFormatting({
    applicableRules: conditionalFormattingRules,
    affectComponent: false,
    affectReference: true,
  });

  return hasComponentsFormatting || hasReferencesFormatting
    ? [
        createSystemFilter(
          hasComponentsFormatting,
          hasReferencesFormatting,
          SystemFilterType.DEFAULT_COLOR
        ),
      ]
    : [];
};

const isDefaultColorSystemFilter = (
  filter: FilterInterfaceFilter | FormattingFilter
) => isSystemFilter(filter) && filter.name === SystemFilterType.DEFAULT_COLOR;

export const hasDefaultGreyColorFilter = (
  formattingFilters: (FilterInterfaceFilter | FormattingFilter)[]
) => formattingFilters.some(isDefaultColorSystemFilter);

const isDefaultColorSystemComponentFilter = (
  filter: FilterInterfaceFilter | FormattingFilter
): boolean => isDefaultColorSystemFilter(filter) && filter.affectComponent;

const isDefaultColorSystemReferenceFilter = (
  filter: FilterInterfaceFilter | FormattingFilter
): boolean => isDefaultColorSystemFilter(filter) && filter.affectReference;

export const hasDefaultGreyColorComponentFilter = (
  formattingFilters: (FilterInterfaceFilter | FormattingFilter)[]
): boolean => formattingFilters.some(isDefaultColorSystemComponentFilter);

export const hasDefaultGreyColorReferenceFilter = (
  formattingFilters: (FilterInterfaceFilter | FormattingFilter)[]
): boolean => formattingFilters.some(isDefaultColorSystemReferenceFilter);

/**
 * Applies grey color to types in the legend if condition is met
 * @param types - An array of types to potentially gray out
 * @param condition - Whether to apply grey color
 * @param colorKey - Which color property to modify ('color' or 'borderColor')
 * @returns The types array with grey color applied if condition is true
 */
export const maybeGrayOutTypes = <T extends object>(
  types: T[],
  condition: boolean,
  colorKey: 'color' | 'borderColor' = 'color'
): T[] =>
  condition
    ? types.map(type => ({ ...type, [colorKey]: vizFilterGray }))
    : types;
